import { NextRouter, useRouter } from "next/router";
import React, { useState, createContext, useContext, useEffect } from "react";

import { useAxios, parseJwt, setWindowVariables } from "../utils/helpers";
import { Loader } from "../design";
import { useQuery } from "@tanstack/react-query";
import { getSessionTokenQuery, getVerifyCustomerTokenQuery } from "../queries/auth.query";
import { Customer, ParsedAccessToken, User } from "../types";
import { safeStorage } from "../utils/helpers";
import { CustomerPortalContext } from "../backend/serverProps";
import useNavigationURL from "../hooks/useNavigationURL";

const LoopAuthContext = createContext(null);

function unsetTokens() {
    safeStorage.removeItem("loopRefreshToken");
    safeStorage.removeItem("loopAccessToken");
}

function logout() {
    unsetTokens();
}

export function LoopAuthProvider(props: { children: React.ReactNode; customerPortalContext: CustomerPortalContext }) {
    const router = useRouter();
    const [axios] = useAxios();
    const navigationUrl = useNavigationURL();

    const [authChecksCompleted, setauthChecksCompleted] = useState(false);
    const [isLoggedIn, setisLoggedIn] = useState(false);
    const [user, setuser] = useState<User | null>(null);
    const [customer, setcustomer] = useState<Customer | null>(null);
    const [storeId, setstoreId] = useState(null);
    const [myshopifyDomain, setmyshopifyDomain] = useState(null);
    const [queryString, setqueryString] = useState("");

    const { data: sessionTokenAuthData, isLoading: sessionTokenAuthIsLoading } = useQuery({
        ...getSessionTokenQuery(router.query.sessionToken as string),
        enabled: !!router.query.sessionToken && router.isReady && !props?.customerPortalContext?.shopifyDomain,
    });

    const { data: customerData, isLoading: customerDataIsLoading } = useQuery({
        ...getVerifyCustomerTokenQuery(
            safeStorage.getItem("loopAccessToken") ?? (router.query.merchantToken as string),
        ),
        enabled: authChecksCompleted && !props?.customerPortalContext?.shopifyDomain,
    });

    function isActiveToken(parsedAccessToken: ParsedAccessToken) {
        if (parsedAccessToken.exp < new Date().getTime() / 1000) {
            return false;
        }
        return true;
    }

    function setUser(parsedAccessToken: ParsedAccessToken) {
        setuser({
            userType: parsedAccessToken.userType,
            userId: parsedAccessToken.userId,
            userShopifyId: parsedAccessToken.userShopifyId,
            context: parsedAccessToken.context,
        });
        setWindowVariables({
            customer: { shopifyId: parsedAccessToken.userShopifyId, email: customer?.email },
        });
        setstoreId(parsedAccessToken.storeId);
        setmyshopifyDomain(parsedAccessToken.myshopifyDomain);
        setisLoggedIn(true);
    }

    async function checkLocalTokens(overrideQS: string) {
        const accessToken: string | undefined = safeStorage.getItem("loopAccessToken");
        const refreshToken: string | undefined = safeStorage.getItem("loopRefreshToken");
        let parsedAccessToken = null;
        let isTokenActive = false;

        try {
            parsedAccessToken = accessToken ? parseJwt(accessToken) : null;
            isTokenActive = parsedAccessToken ? isActiveToken(parsedAccessToken) : false;
        } catch (error) {
            router.push(`${navigationUrl}/get-subscription-link${overrideQS.length ? `?${overrideQS}` : ""}`);
        }

        const loginQueryParams = new URLSearchParams();
        if (parsedAccessToken) {
            loginQueryParams.append("customer_id", parsedAccessToken.userShopifyId);
            loginQueryParams.append("action", parsedAccessToken.context.toLowerCase());
        }

        try {
            if (isTokenActive) {
                setUser(parsedAccessToken);
            } else if (refreshToken) {
                const { data } = await axios.post(
                    `${process.env.NEXT_PUBLIC_AUTH_SERVICE_DOMAIN}/storefront/accessToken`,
                    {
                        refreshToken: refreshToken,
                    },
                );
                const newAccessToken = data.data.accessToken;
                safeStorage.setItem("loopAccessToken", newAccessToken);
                setUser(parseJwt(newAccessToken));
            } else if (!isTokenActive && parsedAccessToken) {
                router.push(`${navigationUrl}/login${overrideQS.length ? `?${overrideQS}` : ""}`);
            } else {
                router.push(`${navigationUrl}/get-subscription-link${overrideQS.length ? `?${overrideQS}` : ""}`);
            }
        } catch (error) {
            if (!isTokenActive && parsedAccessToken) {
                router.push(`${navigationUrl}/login${overrideQS.length ? `?${overrideQS}` : ""}`);
            } else {
                router.push(`${navigationUrl}/get-subscription-link${overrideQS.length ? `?${overrideQS}` : ""}`);
            }
        }
    }

    /**
     * Handles authentication logic based on the presence and validity of tokens in the URL or storage.
     *
     * Priority:
     * 1. **Server Side Auth:** If server-side auth props are available, use them first.
     * 2. **Session Token (URL):** If a `sessionToken` is present in the URL, initiate the token generation process.
     * 3. **Merchant Token (URL):** If a valid `mercchantToken` is found in the URL, set User.
     * 4. **Check Local storage tokens:** If a `refreshToken` is present in storage but no valid `accessToken` is available, use the `refreshToken` to generate a new `accessToken`.
     * 5. **Valid Access Token Check (Storage):** If only access token is present, check if the access token is valid or not, redirect to the login page if token is invalid.
     */
    async function performAuthChecks(urlParams: URLSearchParams) {
        // Priority 1: Use server-side auth if available
        if (props?.customerPortalContext?.shopifyDomain) {
            try {
                const parsedAccessToken = parseJwt(props.customerPortalContext.loopAccessToken);
                if (isActiveToken(parsedAccessToken)) {
                    // Store tokens in local storage for client-side usage
                    safeStorage.setItem("loopAccessToken", props.customerPortalContext.loopAccessToken);
                    if (props.customerPortalContext.loopRefreshToken) {
                        safeStorage.setItem("loopRefreshToken", props.customerPortalContext.loopRefreshToken);
                    }
                    setUser(parsedAccessToken);
                    setauthChecksCompleted(true);
                    return;
                }
            } catch (error) {
                console.warn("Server-side auth token is invalid:", error);
            }
        }
        // Fall back to existing client-side auth logic
        else if (urlParams.has("sessionToken")) {
            if (sessionTokenAuthData?.data) {
                safeStorage.setItem("loopRefreshToken", sessionTokenAuthData.data.refreshToken);
                safeStorage.setItem("loopAccessToken", sessionTokenAuthData.data.accessToken);
                setUser(parseJwt(sessionTokenAuthData.data.accessToken));
            } else {
                await checkLocalTokens(urlParams.toString());
            }
        } else if (urlParams.has("merchantToken")) {
            setUser(parseJwt(urlParams.get("merchantToken")));
            unsetTokens();
        } else {
            await checkLocalTokens(urlParams.toString());
        }
        setauthChecksCompleted(true);
    }

    async function loadCustomer() {
        const data = props?.customerPortalContext ?? customerData?.data; // Use customerPortalContext if available customerData?.data will be undefined if the auth is server side
        setcustomer({
            first_name: data.customerFirstName,
            last_name: data.customerLastName,
            email: data.customerEmail,
            tags: data.customerTags,
            total_orders_placed_by_customer: data.total_orders_placed_by_customer,
            total_spent_by_customer: data.total_spent_by_customer,
            total_subscriptions_orders_by_customer: data.total_subscriptions_orders_by_customer,
            total_subscriptions_spent_by_customer: data.total_subscriptions_spent_by_customer,
        });
        const currentCustomerProps = (window as any).loopProps?.customer;
        if (currentCustomerProps && Object.keys(currentCustomerProps)?.length) {
            setWindowVariables({
                customer: {
                    ...currentCustomerProps,
                    first_name: data.customerFirstName,
                    last_name: data.customerLastName,
                    email: data.customerEmail,
                    tags: data.customerTags,
                },
            });
        }
    }

    function appendQueryParams(router: NextRouter, urlParams: URLSearchParams) {
        // Define essential parameters that should be preserved in navigation
        const essentialParams = ['merchantToken', 'previewThemeId', 'quickActionUuid', 'sessionToken', 'locale', 'country', 'customerPaymentMethodId', 'cardAddEvent', 'cardUpdateEvent'];

        // Only add essential parameters, filtering out Shopify proxy parameters
        essentialParams.forEach(param => {
            if (router.query[param]) {
                urlParams.append(param, router.query[param] as string);
            }
        });

        // Note: Shopify proxy parameters like 'shop', 'logged_in_customer_id', 
        // 'path_prefix', 'timestamp', 'signature' are intentionally excluded
        // to prevent URL pollution in server-side rendered applications
    }

    useEffect(() => {
        if (user?.userId && !customer && !customerDataIsLoading) {
            loadCustomer();
        }
    }, [user, customer, customerDataIsLoading]);

    useEffect(() => {
        if (router.isReady && !authChecksCompleted) {
            const urlParams = new URLSearchParams();
            appendQueryParams(router, urlParams);
            setqueryString(urlParams.toString());
            if (!router.query.sessionToken || !sessionTokenAuthIsLoading) {
                performAuthChecks(urlParams);
            }
        }
    }, [router.isReady, authChecksCompleted, sessionTokenAuthIsLoading]);

    return (
        <LoopAuthContext.Provider
            value={{
                queryString,
                user,
                isLoggedIn,
                storeId,
                myshopifyDomain,
                authChecksCompleted,
                logout,
                customer,
            }}
        >
            {authChecksCompleted ? (
                props.children
            ) : (
                <div className="loop-background" id="loop-background">
                    {" "}
                    <Loader />
                </div>
            )}
        </LoopAuthContext.Provider>
    );
}

export const useLoopAuth = () => useContext(LoopAuthContext);
